/**
 * HeaderNotifications — Bell icon + notification dropdown panel
 * Extracted from Header.jsx for modularity.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { useImpersonationNavigate } from '../../hooks/useImpersonationNavigate';
import { Bell, BellRing } from 'lucide-react';
import { notificationsApi } from '../../stores/api';
import { usePushNotifications } from '../../hooks/usePushNotifications';
import { formatDateShort, parseUTCDate } from '../../utils/formatters';
import { playNotificationSound } from '../../utils/notificationSound';

export default function HeaderNotifications({ token }) {
    const navigate = useImpersonationNavigate();
    const push = usePushNotifications();
    const [showPanel, setShowPanel] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [notifCount, setNotifCount] = useState(0);
    const prevCountRef = useRef(-1);

    const fetchNotifications = useCallback(async () => {
        try {
            if (!token) return;
            const data = await notificationsApi.get(10);
            setNotifications(data.notifications || []);
            const newCount = data.unreadCount || 0;

            // Play sound when new unread notifications appear (skip initial load)
            if (newCount > prevCountRef.current && prevCountRef.current >= 0) {
                playNotificationSound();
            }
            prevCountRef.current = newCount;
            setNotifCount(newCount);

            // Update taskbar/home screen badge
            if ('setAppBadge' in navigator) {
                if (newCount > 0) {
                    navigator.setAppBadge(newCount).catch(() => {});
                } else {
                    navigator.clearAppBadge().catch(() => {});
                }
            }
        } catch {
            // Non-critical
        }
    }, [token]);

    useEffect(() => {
        fetchNotifications();
        const interval = setInterval(fetchNotifications, 30000);
        return () => clearInterval(interval);
    }, [fetchNotifications]);

    const handleRead = async (id) => {
        await notificationsApi.markRead(id);
        fetchNotifications();
    };
    const handleDismiss = async (id) => {
        await notificationsApi.dismiss(id);
        fetchNotifications();
    };
    const handleMarkAllRead = async () => {
        await notificationsApi.markAllRead();
        fetchNotifications();
        // Clear the taskbar/home screen badge
        if ('clearAppBadge' in navigator) {
            navigator.clearAppBadge().catch(() => {});
        }
    };

    const totalBadge = notifCount;

    return (
        <div style={{ position: 'relative' }}>
            <button
                className="btn btn-icon btn-ghost notification-btn"
                onClick={() => {
                    setShowPanel(!showPanel);
                    // Clear badge when opening panel
                    if (!showPanel && 'clearAppBadge' in navigator) {
                        navigator.clearAppBadge().catch(() => {});
                    }
                }}
            >
                <Bell size={20} />
                {totalBadge > 0 && <span className="notification-badge">{totalBadge > 99 ? '99+' : totalBadge}</span>}
            </button>

            {showPanel && (
                <>
                    <div style={{ position: 'fixed', inset: 0, zIndex: 99 }} onClick={() => setShowPanel(false)} />
                    <div style={{
                        position: 'absolute', right: 0, top: '100%',
                        marginTop: 'var(--spacing-2)', width: '340px',
                        background: 'var(--bg-secondary)', border: '1px solid var(--border-primary)',
                        borderRadius: 'var(--radius-lg)', zIndex: 100,
                        boxShadow: 'var(--shadow-lg)', maxHeight: '420px',
                        display: 'flex', flexDirection: 'column'
                    }}>
                        {/* Header */}
                        <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: 'var(--spacing-3) var(--spacing-4)',
                            borderBottom: '1px solid var(--border-primary)'
                        }}>
                            <h4 style={{ fontWeight: 600, fontSize: '0.9rem' }}>Notifications</h4>
                            {notifCount > 0 && (
                                <button
                                    onClick={handleMarkAllRead}
                                    style={{ fontSize: '0.75rem', color: 'var(--accent-primary)', background: 'none', border: 'none', cursor: 'pointer' }}
                                >
                                    Mark all read
                                </button>
                            )}
                        </div>

                        {/* List */}
                        <div style={{ overflowY: 'auto', flex: 1 }}>
                            {notifications.length === 0 ? (
                                <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.8rem' }}>
                                    No notifications
                                </div>
                            ) : (
                                notifications.map(n => (
                                    <div
                                        key={n.id}
                                        style={{
                                            display: 'flex', gap: '10px', padding: '10px var(--spacing-4)',
                                            borderBottom: '1px solid var(--border-primary)',
                                            background: n.is_read ? 'transparent' : 'rgba(99,102,241,0.05)',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => {
                                            handleRead(n.id);
                                            if (n.action_url) { setShowPanel(false); navigate(n.action_url); }
                                        }}
                                    >
                                        <div style={{
                                            width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 6,
                                            backgroundColor:
                                                n.severity === 'critical' ? 'var(--error, #ef4444)' :
                                                    n.severity === 'warning' ? 'var(--warning, #f59e0b)' :
                                                        'var(--info, #3b82f6)'
                                        }} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ fontWeight: n.is_read ? 400 : 600, fontSize: '0.8rem', marginBottom: 2 }}>{n.title}</div>
                                            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.message}</div>
                                            <div style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)', marginTop: 4 }}>
                                                {formatDateShort(n.created_at)} {parseUTCDate(n.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleDismiss(n.id); }}
                                            style={{ background: 'none', border: 'none', color: 'var(--text-tertiary)', cursor: 'pointer', padding: '4px', fontSize: '14px', flexShrink: 0 }}
                                            title="Dismiss"
                                        >✕</button>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Footer — push notification toggle + inbox link */}
                        <div style={{ padding: 'var(--spacing-2) var(--spacing-4)', borderTop: '1px solid var(--border-primary)', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {push.isSupported && !push.isSubscribed && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                    <button
                                        onClick={() => push.subscribe()}
                                        disabled={push.isLoading || push.permission === 'denied'}
                                        style={{
                                            width: '100%', fontSize: '0.75rem', padding: '6px 0',
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            color: 'var(--success, #22c55e)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px'
                                        }}
                                    >
                                        <BellRing size={14} />
                                        {push.isLoading ? 'Processing...' :
                                            push.permission === 'denied' ? 'Notifications blocked in browser' :
                                                'Enable push notifications'}
                                    </button>
                                    {push.permission === 'denied' && (
                                        <div style={{ fontSize: '0.65rem', color: 'var(--text-tertiary)', textAlign: 'center', lineHeight: 1.3 }}>
                                            Click the lock icon in address bar → Notifications → Allow
                                        </div>
                                    )}
                                </div>
                            )}
                            {push.isSupported && push.isSubscribed && (
                                <div style={{ fontSize: '0.7rem', color: 'var(--success, #22c55e)', textAlign: 'center', padding: '4px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                    <BellRing size={14} />
                                    Push notifications enabled ✓
                                </div>
                            )}
                            <button
                                onClick={() => { setShowPanel(false); navigate('/inbox'); }}
                                style={{ width: '100%', fontSize: '0.8rem', color: 'var(--accent-primary)', background: 'none', border: 'none', cursor: 'pointer', padding: '6px 0' }}
                            >
                                Go to Inbox
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
